import { HYDRATE } from 'next-redux-wrapper';

import { IOption } from '@r1-frontend/ui-react/components/select/select';
import { IMethodData } from '@r1-frontend/ui-react/organisms/Payment/PaymentMethod';

import { THydrateAction } from '~/src/store';

import { EPaymentActions,
    EPaymentTypeIds,
    EValidityStatus,
    IAnalyticsData,
    IPaymentResult,
    TClearPaymentMethods,
    TFillBonusesValue,
    TFillEmail,
    TFillPaymentSum,
    TInitPaymentState,
    TPaymentSumValidity,
    TPaymentTransaction,
    TRemovePaymentQrCode,
    TSelectEmail,
    TSetAgreement,
    TSetAnalyticsData,
    TSetBonusesValueValidity,
    TSetCardPaymentSum,
    TSetCreditLoading,
    TSetCredits,
    TSetPaymentMethod,
    TSetPaymentMethods,
    TSetPaymentQrCode,
    TSetPaymentQrLink,
    TSetPaymentResult,
    TSetPaymentSumValidity,
    TSetPaymentTypeId,
    TSetPhone,
    TSetTransactions,
    TToggleCreditSelection,
    TToggleIsShowSpecOffer,
    TTogglePayWithBonusesStatus,
    TToggleSaveCardStatus } from '../actionCreators';

export interface IPaymentData {
    paymentSum: number,
    paymentSumValidity: TPaymentSumValidity,
    cardPaymentSum: number,
    bonusesValue: number,
    isBonusesValueValid: boolean,
    email: string | IOption<string>,
    phone: string,
    isUseBonuses: boolean,
    agreement: string,
    agreementEnc: string,
}

export const initialPaymentData: IPaymentData = {
    paymentSum: 0,
    paymentSumValidity: {
        isValid: EValidityStatus.IS_VALID,
    },
    cardPaymentSum: 0,
    bonusesValue: 0,
    isBonusesValueValid: true,
    email: '',
    phone: '',
    isUseBonuses: false,
    agreement: '',
    agreementEnc: '',
};

export const initialPaymentResult: IPaymentResult = {
    frameUrl: '',
    payStatus: 0,
    email: '',
    queryAgreement: '',
    isOpenPopup: false,
    isPayCardSuccess: false,
    isBonusesSuccess: false,
};

export interface ISbpData {
    qrLink: string,
    qrCode: string,
}

export const initialSbpData: ISbpData = {
    qrLink: '',
    qrCode: '',
};

export interface IPaymentCredit {
    applicationId: string,
    requestDate: string,
    payment: number,
    period: number,
    status: string,
    isSelected: boolean,
}

export interface IPaymentState {
    paymentTypeId: EPaymentTypeIds,
    paymentData: IPaymentData,
    isSaveCard: boolean,
    sbpData: ISbpData,
    methods: IMethodData[],
    method: IMethodData | null,
    result: IPaymentResult,
    transactions: TPaymentTransaction[],
    analitycsData: IAnalyticsData | null,
    isShowSpecOffer: boolean,
    credits: IPaymentCredit[],
    isCreditLoading: boolean,
}

export const initialState: IPaymentState = {
    paymentTypeId: EPaymentTypeIds.DEFAULT,
    paymentData: initialPaymentData,
    isSaveCard: false,
    sbpData: initialSbpData,
    methods: [],
    method: null,
    result: initialPaymentResult,
    transactions: [],
    analitycsData: null, // Данные аналитики для прокидывания в экшены вызываемые при оплате (зависит от станицы на которой используются компоненты оплаты)
    isShowSpecOffer: false,
    credits: [],
    isCreditLoading: false,
};

export type TPaymentActions =
    | TInitPaymentState
    | TFillPaymentSum
    | TSetPaymentSumValidity
    | TFillBonusesValue
    | TSetBonusesValueValidity
    | TSetPhone
    | TFillEmail
    | TSelectEmail
    | TSetCardPaymentSum
    | TTogglePayWithBonusesStatus
    | TSetPaymentMethod
    | TSetPaymentTypeId
    | TToggleSaveCardStatus
    | TSetPaymentResult
    | TSetPaymentQrCode
    | TSetPaymentQrLink
    | TRemovePaymentQrCode
    | TSetTransactions
    | TSetAgreement
    | TSetAnalyticsData
    | TSetPaymentMethods
    | TClearPaymentMethods
    | TToggleIsShowSpecOffer
    | TSetCredits
    | TToggleCreditSelection
    | TSetCreditLoading

// eslint-disable-next-line complexity
const payment = (state = initialState, action: TPaymentActions | THydrateAction): IPaymentState => {
    switch (action.type) {
        case HYDRATE:
            return { ...state };
        case EPaymentActions.INIT_PAYMENT_STATE:
            return {
                ...state,
                paymentData: {
                    ...state.paymentData,
                    paymentSum: action.payload.paymentSum,
                    cardPaymentSum: action.payload.paymentSum,
                    email: action.payload.email ?? '',
                    isUseBonuses: action.payload.isUseBonuses,
                    bonusesValue: action.payload.bonusesValue,
                    agreement: action.payload.agreement,
                    agreementEnc: action.payload.agreementEnc,
                },
                isSaveCard: action.payload.isSaveCard,
            };
        case EPaymentActions.FILL_PAYMENT_SUM:
            return {
                ...state,
                paymentData: {
                    ...state.paymentData,
                    paymentSum: action.payload,
                },
            };
        case EPaymentActions.SET_PAYMENT_SUM_VALIDATION:
            return {
                ...state,
                paymentData: {
                    ...state.paymentData,
                    paymentSumValidity: action.payload,
                },
            };
        case EPaymentActions.SET_PHONE:
            return {
                ...state,
                paymentData: {
                    ...state.paymentData,
                    phone: action.payload,
                },
            };
        case EPaymentActions.FILL_EMAIL:
        case EPaymentActions.SELECT_EMAIL:
            return {
                ...state,
                paymentData: {
                    ...state.paymentData,
                    email: action.payload,
                },
            };
        case EPaymentActions.TOGGLE_PAY_WITH_BONUSES:
            return {
                ...state,
                paymentData: {
                    ...state.paymentData,
                    isUseBonuses: action.payload,
                },
            };
        case EPaymentActions.FILL_BONUSES_VALUE:
            return {
                ...state,
                paymentData: {
                    ...state.paymentData,
                    bonusesValue: action.payload,
                },
            };
        case EPaymentActions.SET_BONUSES_VALUE_VALIDATION:
            return {
                ...state,
                paymentData: {
                    ...state.paymentData,
                    isBonusesValueValid: action.payload,
                },
            };
        case EPaymentActions.SET_CARD_SUM:
            return {
                ...state,
                paymentData: {
                    ...state.paymentData,
                    cardPaymentSum: action.payload,
                },
            };
        case EPaymentActions.SET_PAYMENT_METHOD:
            return {
                ...state,
                method: action.payload,
            };
        case EPaymentActions.SET_PAYMENT_TYPE_ID:
            return {
                ...state,
                paymentTypeId: action.payload,
            };
        case EPaymentActions.TOGGLE_SAVE_CARD_STATUS:
            return {
                ...state,
                isSaveCard: action.payload,
            };
        case EPaymentActions.SET_PAYMENT_RESULT:
            return {
                ...state,
                result: action.payload,
            };
        case EPaymentActions.SET_PAYMENT_QR_LINK:
            return {
                ...state,
                sbpData: {
                    ...state.sbpData,
                    qrLink: action.payload,
                },
            };
        case EPaymentActions.SET_PAYMENT_QR_CODE:
            return {
                ...state,
                sbpData: {
                    ...state.sbpData,
                    qrCode: action.payload,
                },
            };
        case EPaymentActions.REMOVE_PAYMENT_QR_CODE:
            return {
                ...state,
                sbpData: initialSbpData,
            };
        case EPaymentActions.SET_TRANSACTIONS:
            return {
                ...state,
                transactions: action.payload,
            };
        case EPaymentActions.SET_AGREEMENT:
            return {
                ...state,
                paymentData: {
                    ...state.paymentData,
                    agreement: action.payload.agreement,
                    agreementEnc: action.payload.agreementEnc,
                },
            };
        case EPaymentActions.SET_ANALYTICS_DATA:
            return {
                ...state,
                analitycsData: action.payload,
            };
        case EPaymentActions.SET_PAY_METHODS:
            return {
                ...state,
                methods: [...action.payload],
            };
        case EPaymentActions.CLEAR_METHODS:
            return {
                ...state,
                methods: [],
            };
        case EPaymentActions.TOGGLE_IS_SHOW_SPECOFFER:
            return {
                ...state,
                isShowSpecOffer: action.payload,
            };
        case EPaymentActions.SET_CREDITS:
            return {
                ...state,
                credits: action.payload,
            };
        case EPaymentActions.SET_CREDIT_LOADING:
            return {
                ...state,
                isCreditLoading: action.payload,
            };
        case EPaymentActions.TOGGLE_CREDIT_SELECTION:
            return {
                ...state,
                credits: state.credits.map(credit =>
                    credit.applicationId === action.payload
                        ? { ...credit, isSelected: !credit.isSelected }
                        : credit),
            };
        default:
            return state;
    }
};

export default payment;
